import type { ComponentType, ReactNode, SVGProps } from 'react';
import { Coins, Gem, Plus } from 'lucide-react';

import { env } from '@/env';
import { useGetWalletsQuery } from '@/services/qbankApi';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

/**
 * Currency HUD — a compact right-aligned row of dark pills (value + icon):
 * coins → gems, with a full-width Shop button directly beneath it. Rendered
 * above the banner in the Hub.
 *
 * Coins are REAL (the default QBank currency wallet). Gems remain a
 * **placeholder** until QBank exposes them. The Shop button is a no-op for now
 * (to be developed — Telegram Stars store). The EXP / CR-Elo wallet is NOT a
 * currency here — its amount + level live in the Ranked section label instead.
 *
 * Visibility mirrors the wallet entitlement gating: hidden on disabled flag /
 * loading / error (a 401 → not entitled → show nothing).
 */
export function CurrencyHud({ className }: { className?: string } = {}) {
  const haptics = useHaptics();
  // NB: questa riga NON reagisce piu' alla compressione del banner. Prima, da
  // compressi, finiva nella colonna stretta accanto al banner e le pastiglie
  // andavano a capo: il portafoglio si spostava e cambiava forma proprio
  // mentre si stava scorrendo. Ora quella colonna ospita il tasto di sezione
  // e le valute restano dove sono, ferme.
  const { data, isLoading, isError } = useGetWalletsQuery(undefined, {
    skip: !env.featureQbankEnabled,
  });

  if (!env.featureQbankEnabled || isLoading || isError) return null;

  const wallets = data ?? [];
  const currency =
    wallets.find((w) => w.level == null && w.isDefault) ?? wallets.find((w) => w.level == null);
  const coinAmount = Number(currency?.amount ?? 0);

  // PLACEHOLDER — le gemme non hanno backend. Restano a zero finché QBank non
  // le espone: un valore inventato mostrerebbe a ogni utente un saldo che non
  // possiede.
  //
  // L'ENERGIA è stata provata e RIMOSSA di proposito. Serviva a impaginare la
  // riga su tre valute nei mockup, ma non ha alcun backend: portata in
  // produzione avrebbe stampato «10/10» identico a ogni utente per sempre —
  // una bugia, non un segnaposto vuoto. La composizione regge lo stesso, le
  // pastiglie si dividono lo spazio in due invece che in tre. Va reintrodotta
  // il giorno che l'energia esiste davvero, non prima.
  const gems = 0;

  // The whole currency row IS the shop entry point: tapping it will open the
  // shop — the place to buy / spend / convert currencies. That section is not
  // built yet (TODO), so for now the tap only gives press feedback.
  const openShop = () => haptics.impact('light'); // TODO: navigate to the shop section (buy/spend/convert)

  return (
    <section aria-label="Wallet" data-tour="currency-hud" className={cn('flex w-full', className)}>
      {/* La riga e' SEMPRE a tutta larghezza, anche a banner compresso: le
          valute non si spostano ne' si rimpiccioliscono mentre si scorre.
          Le pastiglie si dividono lo spazio (`flex-1`) invece di stare alla
          loro larghezza naturale distanziate: distribuendo restavano tre
          isolette con dei vuoti in mezzo, e la riga sembrava sparsa invece che
          piena. Divise, i vuoti finiscono DENTRO le pastiglie, dove leggono
          come respiro. */}
      <button
        type="button"
        onClick={openShop}
        aria-label="Open shop"
        className="flex w-full items-center gap-1.5 transition-opacity active:opacity-80"
      >
        {/* Monete (vere, dal portafoglio QBank). Nessun "+": si guadagnano
            giocando, non si comprano. */}
        <Pill icon={Coins} iconClass="text-amber-400">
          <span className="text-fg">{coinAmount.toLocaleString()}</span>
        </Pill>

        {/* Gemme (segnaposto) */}
        <Pill icon={Gem} iconClass="text-pink-400" buyable>
          <span className="text-fg">{gems.toLocaleString()}</span>
        </Pill>
      </button>
    </section>
  );
}

interface PillProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  iconClass?: string;
  /** Mostra il "+" a sinistra: la valuta si può ricaricare/acquistare. */
  buyable?: boolean;
  children: ReactNode;
}

function Pill({ icon: Icon, iconClass, buyable, children }: PillProps) {
  return (
    // `bg-bg` e non `bg-black/40`: il nero traslucido sopra il verde acqua lo
    // DESATURA, e la superficie che ne esce e' un grigio-verde spento su cui la
    // stroke bianca legge come grigia. Il teal piu' scuro della palette e'
    // altrettanto scuro ma resta in famiglia.
    //
    // "+" a sinistra, valore+icona a destra: i due estremi separano l'AZIONE
    // dal DATO. Le pastiglie senza "+" tengono comunque il suo posto con uno
    // spaziatore invisibile, invece di centrare il contenuto: centrando, il
    // numero della moneta di mezzo cadeva a un'altezza orizzontale diversa
    // dagli altri due e la fila si leggeva storta.
    <div className="card-edge flex min-w-0 flex-1 items-center justify-between gap-1 rounded-full bg-bg py-1.5 pl-1.5 pr-3">
      {/* Il "+" è decorativo: il bersaglio tattile è l'intera riga, che apre il
          negozio. Un bottone dentro un bottone darebbe due aree di tocco
          sovrapposte su pochi pixel, e su mobile perderebbero entrambe. */}
      {buyable ? (
        // Un'ICONA e non il carattere "+".
        // Il riquadro del carattere era centrato al pixel — misurato — ma il
        // DISEGNO no: nei font il "+" sta sull'asse matematico, cioè più in
        // alto della metà del riquadro, e dentro un tondo di sedici pixel quel
        // paio di decimi si vede. L'icona è una croce geometrica, centrata per
        // costruzione, e non dipende dalle metriche di nessun font.
        <span
          aria-hidden
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand text-brand-fg"
        >
          <Plus className="h-2.5 w-2.5" strokeWidth={4} />
        </span>
      ) : (
        <span aria-hidden className="h-4 w-4 shrink-0" />
      )}
      <span className="flex shrink-0 items-center gap-1">
        <span className="flex items-baseline whitespace-nowrap text-xs font-extrabold tabular-nums">
          {children}
        </span>
        <Icon aria-hidden className={cn('h-3.5 w-3.5 shrink-0', iconClass)} strokeWidth={2.5} />
      </span>
    </div>
  );
}
